import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Loader2, AlertTriangle, CheckCircle2, User, Phone, Mail, IdCard, Clock,
  CalendarRange, Sparkles, Shield, MapPin, Calendar,
  Stethoscope, Layers, Syringe, Droplets, Wind, Plus,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { fmtRangeBE } from '../lib/dt.js'

const TH_MONTHS_ABBR = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
function fmtDayBE(ymd) {
  if (!ymd) return ''
  const [y, m, d] = ymd.split('-').map(Number)
  return `${d} ${TH_MONTHS_ABBR[m - 1]} ${y + 543}`
}

const REDIRECT_TO = 'https://www.toothtaxi.co'

export default function RegistrationSurvey() {
  const { token } = useParams()
  const [boot, setBoot]       = useState(null)
  const [error, setError]     = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone]       = useState(false)
  const [wasUpdated, setWasUpdated] = useState(false)

  const [fullname, setFullname] = useState('')
  const [phone,    setPhone]    = useState('')
  const [email,    setEmail]    = useState('')
  const [idcard,   setIdcard]   = useState('')
  const [dayIdx,   setDayIdx]   = useState(0)
  const [slot,     setSlot]     = useState('')
  // Track which fields the user has interacted with so we only surface
  // per-field errors after they've left the input — typing into an empty
  // field should not flash red.
  const [touched, setTouched] = useState({})
  const markTouched = (k) => setTouched((t) => (t[k] ? t : { ...t, [k]: true }))

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const phoneOk  = /^\d{10}$/.test(phone)
  const emailOk  = !email || EMAIL_RE.test(email)
  const idcardOk = /^\d{13}$/.test(idcard)

  useEffect(() => {
    api.publicSurvey.registration.get(token)
      .then((b) => {
        setBoot(b)
        // Auto-select the first day when there is only one option.
        if (b?.days?.length === 1) setDayIdx(0)
      })
      .catch((e) => setError(extractErr(e)))
  }, [token])

  // Reset the selected time when the user changes day so we never carry a
  // slot value that belongs to a different day.
  useEffect(() => { setSlot('') }, [dayIdx])

  const days       = boot?.days || []
  const multiDay   = days.length > 1
  const activeDay  = days[dayIdx] || null
  const activeSlots = activeDay?.slots || []

  useEffect(() => {
    if (!done) return
    // Give the user enough time to read the unconfirmed-slot notice
    // before bouncing them out to the marketing site.
    const t = setTimeout(() => { window.location.href = REDIRECT_TO }, 6000)
    return () => clearTimeout(t)
  }, [done])

  async function submit(e) {
    e.preventDefault()
    setError(null)
    // Surface any latent per-field errors immediately on a submit attempt.
    setTouched({ phone: true, email: true, idcard: true })

    if (!fullname.trim()) return setError('กรุณากรอกชื่อ-สกุล')
    if (!phoneOk)         return setError('เบอร์โทรต้อง 10 หลัก')
    if (!emailOk)         return setError('รูปแบบอีเมลไม่ถูกต้อง')
    if (!idcardOk)        return setError('เลขบัตรประชาชนต้อง 13 หลัก')
    if (!slot)            return setError('กรุณาเลือกช่วงเวลา')

    setSubmitting(true)
    try {
      const res = await api.publicSurvey.registration.submit(token, {
        fullname: fullname.trim(),
        phone,
        email:    email.trim() || undefined,
        idcard,
        time_slot: slot,
      })
      setWasUpdated(Boolean(res?.updated))
      setDone(true)
    } catch (e) {
      setError(extractErr(e))
    } finally {
      setSubmitting(false)
    }
  }

  if (error && !boot) return <Shell><ErrorCard message={error} /></Shell>
  if (!boot)          return <Shell><Centered><Loader2 className="animate-spin text-amber-400" /></Centered></Shell>
  if (done)           return <Shell><ThankYou updated={wasUpdated} /></Shell>

  return (
    <Shell>
      <Hero />

      {/* Intro card — what Tooth Taxi is, why employees should use it */}
      <section className="mt-6 bg-white rounded-3xl shadow-[0_24px_60px_-24px_rgba(217,119,6,0.18)] ring-1 ring-amber-100/70 p-6 md:p-7">
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 ring-1 ring-amber-200 text-amber-700 text-[12px] font-semibold uppercase tracking-wider">
            <Sparkles size={12} /> ลงทะเบียนสนใจรับบริการ
          </div>
          <h1 className="mt-3 text-[26px] md:text-[28px] font-extrabold text-zinc-900 leading-tight tracking-tight">
            ทำฟันกับ Tooth Taxi <span className="text-amber-600">ฟรี</span> ที่หน้า office
          </h1>
          <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
            <span className="font-semibold text-amber-700">หมดปัญหาไม่มีเวลาไปหาหมอฟัน!</span>{' '}
            <span className="font-semibold text-zinc-900">Tooth Taxi</span> รถทันตกรรมเคลื่อนที่พร้อมให้บริการ
            <span className="font-medium"> ตรวจ ขูด อุด ถอน</span> ถึงหน้าบริษัทคุณ
            ดูแลโดย<span className="font-medium">ทันตแพทย์ผู้เชี่ยวชาญ</span> พร้อมอุปกรณ์ครบครันและได้มาตรฐาน
            ประหยัดเวลาทำงาน ไม่ต้องลางานไปคลินิก
            <br className="hidden md:block" />
            สามารถใช้สิทธิ<span className="font-semibold">ประกันสังคม</span> / หรือ<span className="font-semibold">สวัสดิการของบริษัท</span>ได้เลย!
          </p>
        </div>

        {/* Service highlights */}
        <ul className="mt-5 grid grid-cols-3 gap-2 text-center">
          <Bullet icon={Shield}    title="ทำฟันฟรี!"              subtitle="เบิกได้ ไม่ต้องสำรองจ่าย" />
          <Bullet icon={MapPin}    title="ทำฟันหน้าบริษัท"        subtitle="ไม่ต้องลางาน" />
          <Bullet icon={Sparkles}  title="ทันตแพทย์มืออาชีพ"      subtitle="ส่งตรงจากคลินิก" />
        </ul>

        {/* Event window */}
        <div className="mt-5 inline-flex items-center gap-2 w-full justify-center px-4 py-3 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 ring-1 ring-amber-200/70 text-[14.5px] text-zinc-700">
          <CalendarRange size={16} className="text-amber-600 shrink-0" />
          <span className="font-medium tabular-nums">{fmtRangeBE(boot.operation.start_date, boot.operation.end_date, boot.operation.start_time, boot.operation.end_time)}</span>
        </div>
      </section>

      {/* Services available in the mobile unit — shown only when staff enable it
          per-operation from the Patient list tab's registration-link card. */}
      {boot.show_services && (
      <section className="mt-4 bg-white rounded-3xl shadow-[0_24px_60px_-24px_rgba(217,119,6,0.18)] ring-1 ring-amber-100/70 p-6 md:p-7">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-amber-50 ring-1 ring-amber-200 text-amber-600 shrink-0">
            <Stethoscope size={17} />
          </span>
          <div>
            <h2 className="text-[17px] font-bold text-zinc-900 leading-tight">บริการที่ทำได้ในรถ Tooth Taxi</h2>
            <p className="text-[12.5px] text-zinc-500">ทันตแพทย์ดูแลครบ จบในที่เดียว</p>
          </div>
        </div>

        <ul className="mt-4 flex flex-wrap justify-center gap-2.5">
          <ServiceChip icon={Sparkles} label="ขูดหินปูน" />
          <ServiceChip icon={Layers}   label="อุดฟัน" />
          <ServiceChip icon={Syringe}  label="ถอนฟัน" />
          <ServiceChip icon={Droplets} label="เคลือบฟลูออไรด์" />
          <ServiceChip icon={Wind}     label="AirFlow" />
          <ServiceChip icon={Plus}     label="ทันตกรรมอื่นๆ" />
        </ul>
      </section>
      )}

      <form onSubmit={submit} className="mt-4 bg-white rounded-3xl shadow-[0_24px_60px_-24px_rgba(217,119,6,0.25)] ring-1 ring-amber-100/70 p-6 md:p-8 space-y-5">
        <div className="pb-1 border-b border-zinc-100">
          <h2 className="text-[18px] font-bold text-zinc-900">กรอกข้อมูลของคุณ</h2>
          <p className="mt-1 text-[13.5px] text-zinc-500">เจ้าหน้าที่จะติดต่อยืนยันรายละเอียดอีกครั้ง</p>
        </div>

        <Field icon={User} label="ชื่อ-สกุล" required>
          <input value={fullname} onChange={(e) => setFullname(e.target.value)}
            placeholder="เช่น สมชาย ใจดี" maxLength={200} className={input} />
        </Field>

        <Field icon={Phone} label="เบอร์โทรศัพท์" hint="10 หลัก" required
          error={touched.phone && !phoneOk ? 'เบอร์โทรต้อง 10 หลัก' : ''}>
          <input type="tel" inputMode="numeric" value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
            onBlur={() => markTouched('phone')}
            placeholder="0812345678" maxLength={10}
            aria-invalid={touched.phone && !phoneOk}
            className={input + ' tabular-nums' + (touched.phone && !phoneOk ? ' !ring-rose-400 focus:!ring-rose-400 bg-rose-50/40' : '')} />
        </Field>

        <Field icon={Mail} label="อีเมล" hint="ไม่บังคับ"
          error={touched.email && !emailOk ? 'รูปแบบอีเมลไม่ถูกต้อง' : ''}>
          <input type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => markTouched('email')}
            placeholder="name@example.com" maxLength={200}
            autoComplete="email" spellCheck={false}
            aria-invalid={touched.email && !emailOk}
            className={input + (touched.email && !emailOk ? ' !ring-rose-400 focus:!ring-rose-400 bg-rose-50/40' : '')} />
        </Field>

        <Field icon={IdCard} label="เลขบัตรประชาชน" hint="13 หลัก" required
          error={touched.idcard && !idcardOk ? 'เลขบัตรประชาชนต้อง 13 หลัก' : ''}>
          <input inputMode="numeric" value={idcard}
            onChange={(e) => setIdcard(e.target.value.replace(/[^\d]/g, '').slice(0, 13))}
            onBlur={() => markTouched('idcard')}
            placeholder="1234567890123" maxLength={13}
            aria-invalid={touched.idcard && !idcardOk}
            className={input + ' tabular-nums tracking-[0.04em]' + (touched.idcard && !idcardOk ? ' !ring-rose-400 focus:!ring-rose-400 bg-rose-50/40' : '')} />
        </Field>

        {days.length === 0 ? (
          <Field icon={Clock} label="ช่วงเวลาที่สะดวก" required>
            <div className="text-[12.5px] text-rose-600 px-1">ไม่พบช่วงเวลาที่สามารถเลือกได้</div>
          </Field>
        ) : (
          <>
            {multiDay && (
              <Field icon={Calendar} label="วันที่สะดวก" required>
                <select
                  value={dayIdx}
                  onChange={(e) => setDayIdx(Number(e.target.value))}
                  className={input + ' pr-9 appearance-none bg-white'}
                  style={{ backgroundImage: chevronBg, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '12px' }}
                >
                  {days.map((d, i) => (
                    <option key={d.date} value={i}>{fmtDayBE(d.date)}</option>
                  ))}
                </select>
              </Field>
            )}

            <Field icon={Clock} label="ช่วงเวลาที่สะดวก" required>
              <select
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                className={input + ' pr-9 appearance-none bg-white tabular-nums'}
                style={{ backgroundImage: chevronBg, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '12px' }}
              >
                <option value="">— เลือกช่วงเวลา —</option>
                {activeSlots.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>
          </>
        )}

        {error && (
          <div className="px-3.5 py-2.5 rounded-xl bg-rose-50 ring-1 ring-rose-200/80 text-rose-700 text-[14px] flex items-center gap-2">
            <AlertTriangle size={15} className="shrink-0" /> {error}
          </div>
        )}

        <button type="submit" disabled={submitting}
          className="group w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-[16px] font-semibold shadow-[0_10px_24px_-8px_rgba(249,115,22,0.55)] transition-all active:scale-[0.99] disabled:opacity-60">
          {submitting
            ? <Loader2 size={18} className="animate-spin" />
            : <span>ส่งข้อมูลลงทะเบียน</span>}
        </button>

        <p className="text-center text-[12.5px] text-zinc-400 pt-1">
          ข้อมูลของคุณจะถูกใช้เพื่อจัดเตรียมบริการเท่านั้น
        </p>
      </form>

      <Footer />
    </Shell>
  )
}


/* ──────────────────────────────── shared bits ──────────────────────────── */

function Hero() {
  return (
    <div className="relative overflow-hidden rounded-3xl shadow-[0_24px_60px_-30px_rgba(217,119,6,0.45)]">
      <img
        src="/hero-toothtaxi.jpg"
        alt="Tooth Taxi — Dental Service on the Go"
        className="w-full h-auto block"
        loading="eager"
      />
    </div>
  )
}

function ThankYou({ updated }) {
  return (
    <div className="bg-white rounded-3xl shadow-[0_24px_60px_-24px_rgba(16,185,129,0.35)] ring-1 ring-emerald-100 p-8 md:p-10 text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 ring-8 ring-emerald-50/60 flex items-center justify-center">
        <CheckCircle2 size={36} className="text-emerald-500" strokeWidth={2.25} />
      </div>
      <h2 className="mt-4 text-[24px] font-bold text-zinc-900">
        {updated ? 'อัปเดตข้อมูลเรียบร้อย' : 'ขอบคุณที่ลงทะเบียน'}
      </h2>
      <p className="mt-2 text-[14.5px] text-zinc-500">
        {updated
          ? 'พบรายชื่อของคุณในระบบแล้ว — เราได้อัปเดตข้อมูลล่าสุดให้เรียบร้อย'
          : 'เจ้าหน้าที่จะติดต่อยืนยันรายละเอียดอีกครั้ง'}
      </p>

      {/* Soft amber disclaimer — the chosen time slot is a preference, not a guarantee. */}
      <div className="mt-5 mx-auto max-w-[420px] text-left flex items-start gap-2 px-4 py-3 rounded-2xl bg-amber-50 ring-1 ring-amber-200/70 text-[13px] text-amber-800 leading-relaxed">
        <Clock size={16} className="shrink-0 mt-0.5 text-amber-600" />
        <span>
          <span className="font-semibold">ช่วงเวลาที่เลือกยังไม่ได้รับการยืนยัน</span>{' '}
          เจ้าหน้าที่จะพยายามจัดคิวให้ตามที่คุณเลือกอย่างเต็มที่
          และจะติดต่อกลับเพื่อยืนยันเวลาที่แน่นอนอีกครั้ง
        </span>
      </div>

      <div className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] text-zinc-400">
        <Loader2 size={12} className="animate-spin" />
        กำลังพากลับไปยัง toothtaxi.co…
      </div>
    </div>
  )
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50/40 to-white">
      <div className="max-w-[540px] mx-auto px-4 py-6 md:py-10">{children}</div>
    </div>
  )
}

function Centered({ children }) {
  return <div className="min-h-[60vh] flex items-center justify-center">{children}</div>
}

function ErrorCard({ message }) {
  return (
    <div className="bg-white rounded-3xl ring-1 ring-rose-200/60 shadow-xl p-8 text-center">
      <AlertTriangle size={36} className="mx-auto text-rose-500" />
      <h2 className="mt-3 text-[19px] font-semibold text-zinc-900">ลิงก์นี้ใช้งานไม่ได้</h2>
      <p className="mt-2 text-[14px] text-zinc-500">{message}</p>
    </div>
  )
}

function Footer() {
  return (
    <p className="text-center text-[12px] text-zinc-400 mt-5">
      Powered by <span className="font-semibold text-amber-700">Tooth Taxi</span> · Dental Service on the Go
    </p>
  )
}

function Field({ icon: Icon, label, hint, required, error, children }) {
  return (
    <label className="block">
      <span className="flex items-center justify-between mb-2 px-0.5">
        <span className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-zinc-700">
          {Icon && <Icon size={15} className="text-amber-500" />} {label}
          {required && <span className="text-rose-500">*</span>}
        </span>
        {hint && <span className="text-[12.5px] text-zinc-400">{hint}</span>}
      </span>
      {children}
      {error && (
        <span className="mt-1.5 block px-0.5 text-[12.5px] text-rose-600">{error}</span>
      )}
    </label>
  )
}

const input = 'w-full px-3.5 py-3 bg-zinc-50/70 rounded-xl text-[15.5px] text-zinc-900 placeholder:text-zinc-400 ring-1 ring-zinc-200 focus:bg-white focus:ring-2 focus:ring-amber-400 focus:outline-none transition-all'

// Inline SVG chevron data-URI so the native <select> arrow uses our color.
const chevronBg = 'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%23a1a1aa\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")'

function ServiceChip({ icon: Icon, label }) {
  return (
    <li className="inline-flex items-center gap-2 pl-2 pr-3.5 py-2 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50/60 ring-1 ring-amber-200/70">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white ring-1 ring-amber-200 text-amber-600 shrink-0">
        <Icon size={15} />
      </span>
      <span className="text-[13.5px] font-semibold text-zinc-800 leading-tight">{label}</span>
    </li>
  )
}

function Bullet({ icon: Icon, title, subtitle }) {
  return (
    <li className="flex flex-col items-center gap-1 px-1.5 py-2.5 rounded-xl bg-zinc-50/60 ring-1 ring-zinc-200/60">
      <Icon size={18} className="text-amber-500" />
      <span className="text-[13px] font-semibold text-zinc-900 leading-tight">{title}</span>
      <span className="text-[11.5px] text-zinc-500 leading-tight">{subtitle}</span>
    </li>
  )
}

function extractErr(e) {
  const m = (e?.message || '').match(/:\s*\{?"?error"?"?:\s*"?([^"}\n]+)/)
  return m ? m[1] : (e?.message || 'error')
}
